import { useMemo, useState, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { Loader2, X, ArrowRight, RotateCcw, PiggyBank, Percent, Package } from 'lucide-react'
import { TopSection } from '../../ui/TopSection'
import { StatCard } from '../../ui/StatCard'
import { metricIcon } from '../../ui/metricIcons'
import { TimeViewOption } from '../../ui/TimeControls'
import { useSourcingRunStore } from '../../../stores/appSourcingRunStore'
import { getSourcingComparison, getSourcingVolumes, canCompareSourcingUnits, isSourcingSelf, sourcingDisplayUnitPrice } from '../../../services/sourcing/sourcingRunCalculations'
import { SourcingRunProduct } from '../../../services/sourcing/sourcingRunTypes'
import { useRestaurantStore } from '../../../stores/appRestaurantStore'
import { getCategoryDisplayName } from '../../orderhistory/categoryUtils'
import { useAppUserDefaultStore, saveVatOrdersSetting } from '../../../stores/appUserDefaultStore'
import { useProductSearch } from '../../../hooks/useProductSearch'
import { calculateEndDate, startOfPeriod, normalizeDate } from '../../../utils/dateUtils'
import SourcingRestaurantPicker from './SourcingRestaurantPicker'
import { Platform } from '../../../types/Platforms'
import { cn } from '../../../lib/utils'
import { SupplierLogo } from '../../ui/SupplierLogo'
import { useAppSettingsStore } from '../../../stores/appSettingsStore'
import {
  whiteBG,
  primaryText,
  smallTextSize,
  mediumText,
  semiBoldText,
  boldText,
  tertiaryText,
  borderPrimary,
  extraSmallTextSize,
  baseTextSize,
  largeTextSize,
  iconBgGreen,
  iconBgBlue,
} from '../../../styling/stylingHelper'

interface OrderHistorySourcingViewProps {
  entryKey: string
  onGoBack: () => void
  onRestart: () => void
}

type Option = SourcingRunProduct & { matchScore: number | null }
interface Tiers { cheaper: Option[]; uncomparable: Option[]; expensive: Option[] }
const countTieredOptions = (tiers: Tiers) => tiers.cheaper.length + tiers.uncomparable.length + tiers.expensive.length

function PlatformLogo({ platform, size = 'medium' }: { platform: string; size?: 'small' | 'medium' }) {
  return <SupplierLogo platform={Platform.fromString(platform)} size={size} alt={platform} />
}

export default function OrderHistorySourcingView({ entryKey, onGoBack, onRestart }: OrderHistorySourcingViewProps) {
  const { t } = useTranslation()
  const darkMode = useAppSettingsStore(s => s.darkMode)
  const showExVat = useAppUserDefaultStore(s => s.showExVatOrders)
  const run = useSourcingRunStore(s => s.runs[entryKey])
  const restaurants = useRestaurantStore(s => s.restaurants)
  const selectedTimeOption = run?.viewOption || 'week'
  const [detailArticleId, setDetailArticleId] = useState<string | null>(null)
  const [isPanelVisible, setIsPanelVisible] = useState(false)
  const [query, setQuery] = useState('')
  const [debouncedQuery, setDebouncedQuery] = useState('')
  const [category, setCategory] = useState('')
  const [supplier, setSupplier] = useState('')
  const [searchLimit, setSearchLimit] = useState(20)
  useEffect(() => {
    const timeout = setTimeout(() => { setDebouncedQuery(query); setSearchLimit(20) }, 200)
    return () => clearTimeout(timeout)
  }, [query])

  const snapshot = run?.snapshot
  const loading = run?.status === 'loading'
  const volumesReady = run?.volume.status === 'ready'
  const volumeOrders = run?.volume.orders
  const viewRestaurantIds = run?.viewRestaurantIds
  const period = run?.period
  const originals = snapshot?.originals
  const volumes = useMemo(() => originals && volumeOrders && viewRestaurantIds && period ? getSourcingVolumes(volumeOrders, originals, viewRestaurantIds, period) : {}, [originals, volumeOrders, viewRestaurantIds, period])
  const tieredMap = useMemo(() => {
    const map: Record<string, Tiers> = {}
    if (!snapshot) return map
    for (const parent of Object.values(snapshot.originals)) {
      const scores = new Map((snapshot.options[parent.key] || []).map(o => [o.productKey, o.matchScore]))
      const keys = new Set([...Array.from(scores.keys()), ...(run?.manualOptions[parent.key] || [])])
      const tiers: Tiers = { cheaper: [], uncomparable: [], expensive: [] }
      for (const key of Array.from(keys)) {
        const product = snapshot.catalog[key]
        if (!product || isSourcingSelf(parent, product)) continue
        const option = { ...product, matchScore: scores.get(key) ?? null }
        if (!canCompareSourcingUnits(parent.unit, option.unit)) tiers.uncomparable.push(option)
        else if (parent.unitPrice !== null && option.unitPrice !== null && option.unitPrice < parent.unitPrice) tiers.cheaper.push(option)
        else tiers.expensive.push(option)
      }
      tiers.cheaper.sort((a, b) => (a.unitPrice ?? Infinity) - (b.unitPrice ?? Infinity))
      tiers.expensive.sort((a, b) => (a.unitPrice ?? Infinity) - (b.unitPrice ?? Infinity))
      tiers.uncomparable.sort((a, b) => (b.matchScore ?? -1) - (a.matchScore ?? -1))
      map[parent.key] = tiers
    }
    return map
  }, [snapshot, run?.manualOptions])

  const sortedEntries = useMemo(() => {
    if (!snapshot || !run) return []
    return Object.values(snapshot.originals).map(parent => {
      const selected = run.selections[parent.key] === parent.key ? parent : snapshot.catalog[run.selections[parent.key]] || parent
      const comparison = getSourcingComparison(parent, selected, volumes[parent.key] || { quantity: 0, valid: false }, showExVat)
      return { articleId: parent.key, parent, selected, currentSpend: comparison.currentCost, projectedSpend: comparison.alternativeCost, savings: comparison.savings, savingsPercent: comparison.savingsPercent, isSameProduct: parent.key === selected.key, comparison }
    }).sort((a, b) => (b.savings || 0) - (a.savings || 0))
  }, [snapshot, run, volumes, showExVat])
  const totalCurrentSpend = sortedEntries.reduce((sum, entry) => sum + (entry.currentSpend || 0), 0)
  const totalSavings = sortedEntries.reduce((sum, entry) => sum + (entry.savings || 0), 0)
  const totalSavingsPercent = totalCurrentSpend > 0 ? totalSavings / totalCurrentSpend * 100 : 0
  const detailParent = detailArticleId ? snapshot?.originals[detailArticleId] : undefined
  const detailTiers = detailArticleId ? tieredMap[detailArticleId] : undefined
  const detailCurrentSpend = sortedEntries.find(entry => entry.articleId === detailArticleId)?.currentSpend
  const catalogue = useMemo(() => Object.values(snapshot?.catalog || {}), [snapshot])
  const categories = useMemo(() => Array.from(new Set(catalogue.map(p => p.category).filter(Boolean))).sort(), [catalogue])
  const suppliers = useMemo(() => Array.from(new Set(catalogue.map(p => p.supplierName || p.platform))).sort(), [catalogue])
  const searchCatalogue = useMemo(() => catalogue.filter(p => (!category || p.category === category) && (!supplier || (p.supplierName || p.platform) === supplier) && (!detailParent || !isSourcingSelf(detailParent, p))), [catalogue, category, supplier, detailParent])
  const search = useProductSearch({ data: searchCatalogue, keys: ['name', 'articleId'], query: debouncedQuery, limit: searchLimit })
  const money = (value: number | null | undefined) => value == null || !volumesReady ? '--' : `€${value.toFixed(2)}`
  const formatUnitPrice = (product: SourcingRunProduct) => {
    const price = sourcingDisplayUnitPrice(product, showExVat)
    const unit = product.unit === 'g' ? 'kg' : product.unit === 'ml' ? 'l' : t('products.sourcing.piece')
    return price === null || !product.unit ? '--' : `€${price.toFixed(2)}/${unit}`
  }
  const setDateRange = (range: { start: Date; end: Date }, viewOption = selectedTimeOption) => {
    const end = new Date(range.end)
    end.setHours(23, 59, 59, 999)
    if (run) void useSourcingRunStore.getState().setView(entryKey, { start: normalizeDate(range.start).toISOString(), end: end.toISOString() }, run.viewRestaurantIds, viewOption)
  }
  const setSelectedTimeOption = (option: TimeViewOption) => {
    const start = option === 'all' ? new Date('2020-01-01') : startOfPeriod(new Date(), option)
    setDateRange({ start, end: option === 'all' ? new Date() : calculateEndDate(start, option) }, option)
  }
  const openDetail = (key: string) => {
    setDetailArticleId(key)
    setQuery(''); setDebouncedQuery(''); setCategory(''); setSupplier(''); setSearchLimit(20)
    requestAnimationFrame(() => setIsPanelVisible(true))
  }
  const closeDetail = () => { setIsPanelVisible(false); setDetailArticleId(null) }
  const handleSelectOption = (key: string, option: SourcingRunProduct) => {
    if (option.key !== key) useSourcingRunStore.getState().addManualOption(entryKey, key, option.key)
    useSourcingRunStore.getState().selectOption(entryKey, key, option.key)
  }
  const renderEvidence = (product: SourcingRunProduct) => <div className={cn(tertiaryText, extraSmallTextSize, 'mt-0.5')}>
    {[product.supplierName || product.platform, product.restaurantName, (product.orderDate || run?.historicalOrderDates[product.key]) ? new Date((product.orderDate || run?.historicalOrderDates[product.key])!).toLocaleDateString() : null].filter(Boolean).join(' · ')}
  </div>
  const renderVolume = (key: string, product: SourcingRunProduct) => <div className={cn(tertiaryText, extraSmallTextSize, 'mt-0.5')}>
    {volumesReady && volumes[key]?.valid ? `${volumes[key].quantity.toFixed(2)} ${product.unit === 'g' ? 'kg' : product.unit === 'ml' ? 'l' : t('products.sourcing.piece')}` : '--'} {t('products.ordered')}
  </div>

  const renderOption = (option: Option, idx: number, showSavings: boolean) => {
    if (!detailParent || !detailArticleId) return null
    const isSelected = run?.selections[detailArticleId] === option.key
    const comparison = getSourcingComparison(detailParent, option, volumes[detailArticleId] || { quantity: 0, valid: false }, showExVat)
    const selectable = option.key === detailParent.key || (canCompareSourcingUnits(detailParent.unit, option.unit) && option.unitPrice !== null && option.unitPrice > 0)
    const optionSavings = comparison.savings || 0
    const optionSavingsPercent = comparison.savingsPercent || 0
    const projectedSpend = comparison.alternativeCost

    return (
      <div
        key={`${option.key}-${idx}`}
        role="button" tabIndex={selectable ? 0 : -1} aria-disabled={!selectable}
        onKeyDown={event => { if (selectable && (event.key === 'Enter' || event.key === ' ')) { event.preventDefault(); handleSelectOption(detailArticleId, option) } }}
        className={cn(
          "rounded-xl border p-4 transition-all", selectable ? "cursor-pointer" : "opacity-60 cursor-not-allowed",
          borderPrimary,
          isSelected
            ? "border-green-400 bg-green-50/50 dark:bg-green-950/20 ring-1 ring-green-400 dark:ring-green-600"
            : "hover:border-gray-300 dark:hover:border-gray-600"
        )}
        onClick={() => { if (selectable) handleSelectOption(detailArticleId, option) }}
      >
        {/* Top row: logo + name + badges + savings */}
        <div className="flex items-start gap-3">
          <div className="shrink-0 w-9 h-9 rounded-lg bg-gray-50 dark:bg-gray-800/50 flex items-center justify-center">
            <PlatformLogo platform={option.platform.toString()} size="small" />
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2">
              <span className={cn(primaryText, smallTextSize, semiBoldText, "truncate")}>
                {option.name}
              </span>
              {isSelected && (
                <span className="shrink-0 text-xs px-1.5 py-0.5 rounded-full bg-green-100 dark:bg-green-900 text-green-700 dark:text-green-300">
                  {t('products.selectedLabel')}
                </span>
              )}
            </div>
            {renderEvidence(option)}
            <div className={cn(tertiaryText, extraSmallTextSize)}>{option.key === detailParent.key ? t('products.sourcing.original') : option.matchScore === null ? t('products.sourcing.manual') : t('products.sourcing.matchScore', { score: option.matchScore })}</div>
            {comparison.approximate && <div className={cn(tertiaryText, extraSmallTextSize)}>{t('products.sourcing.approximate')}</div>}
            {!selectable && <div className={cn(tertiaryText, extraSmallTextSize)}>{t('products.sourcing.incompatible')}</div>}

          </div>

          {/* Savings badge on right (only for comparable tiers) */}
          {showSavings && volumesReady && optionSavings !== 0 && (
            <div className={cn(
              "shrink-0 text-center px-3 py-1.5 rounded-lg",
              optionSavings > 0 ? "bg-green-50 dark:bg-green-950/30" : "bg-red-50 dark:bg-red-950/30"
            )}>
              <div
                className={cn(smallTextSize, boldText)}
                style={{ color: optionSavings > 0 ? (darkMode ? '#34D399' : '#16a34a') : (darkMode ? '#FCA5A5' : '#ef4444') }}
              >
                {optionSavings > 0 ? '-' : '+'}{Math.abs(optionSavingsPercent).toFixed(0)}%
              </div>
              <div
                className={extraSmallTextSize}
                style={{ color: optionSavings > 0 ? (darkMode ? '#34D399' : '#16a34a') : (darkMode ? '#FCA5A5' : '#ef4444') }}
              >
                €{Math.abs(optionSavings).toFixed(2)}
              </div>
            </div>
          )}
        </div>

        {/* Bottom row: unit price (big) + projected period spend (comparable only) */}
        <div className="flex items-baseline gap-4 mt-3 pl-12">
          <span className={cn(primaryText, baseTextSize, boldText)}>
            {formatUnitPrice(option)}
          </span>
          {showSavings && detailCurrentSpend != null && detailCurrentSpend > 0 && (
            <span className={cn(tertiaryText, extraSmallTextSize)}>
              {money(projectedSpend)} {t('products.inPeriod')}
            </span>
          )}
        </div>
      </div>
    )
  }

  const tierSection = (title: string, hint: string | null, options: Option[], showSavings: boolean) => {
    if (options.length === 0) return null
    return (
      <div className="flex flex-col gap-2.5">
        <div className="px-1 pt-1">
          <div className={cn(tertiaryText, extraSmallTextSize, "uppercase tracking-wide font-medium")}>
            {title} ({options.length})
          </div>
          {hint && <div className={cn(tertiaryText, extraSmallTextSize, "mt-0.5")}>{hint}</div>}
        </div>
        {options.map((option, idx) => renderOption(option, idx, showSavings))}
      </div>
    )
  }

  if (!run) return <button onClick={onGoBack} className={cn(primaryText, smallTextSize)}>{t('products.cancel')}</button>

  return (
    <div>
      <TopSection
        title={t('products.improveSourcing')}
        showMobileTitle
        backButton={true}
        handleGoBack={onGoBack}
        dateRange={{ start: new Date(run.period.start), end: new Date(run.period.end) }}
        setDateRange={setDateRange}
        selectedTimeOption={selectedTimeOption}
        setSelectedTimeOption={setSelectedTimeOption}
        currentVatValue={showExVat ? 'excl' : 'incl'}
        onVatChange={value => { void saveVatOrdersSetting(value === 'excl').catch(error => console.error('Failed to save VAT settings', error)) }}
      />

      <div className="flex flex-col gap-6">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div className={cn(tertiaryText, extraSmallTextSize)}>
            {snapshot && <div>{t('products.sourcing.generatedAt', { date: new Date(snapshot.generatedAt).toLocaleString() })}</div>}
            <div>{t('products.sourceFrom')}: {restaurants.filter(r => run.request.sourceFromRestaurantIds.includes(r.id)).map(r => r.name).join(', ')}</div>
          </div>
          {run.request.targetRestaurantIds.length > 1 && <SourcingRestaurantPicker restaurants={restaurants.filter(r => run.request.targetRestaurantIds.includes(r.id))} selectedIds={run.viewRestaurantIds} onChange={ids => { void useSourcingRunStore.getState().setView(entryKey, run.period, ids) }} label={t('products.sourcing.viewRestaurants')} />}
          <button type="button" onClick={onRestart} disabled={loading} className={cn(whiteBG, borderPrimary, primaryText, smallTextSize, 'flex items-center gap-2 rounded-lg border px-3 py-2 disabled:opacity-50')}>
            <RotateCcw className="h-4 w-4" />{t('products.sourcing.restart')}
          </button>
        </div>
        <p className={cn(tertiaryText, extraSmallTextSize)}>{t('products.sourcing.estimateHint')}</p>
        {run.error && <p role="alert" className={cn(primaryText, smallTextSize)}>{t('products.sourcing.runError')}</p>}
        {run.volume.status === 'error' && <p role="alert" className={cn(primaryText, smallTextSize)}>{t('products.sourcing.volumeError')} <button className="underline" onClick={() => { void useSourcingRunStore.getState().setView(entryKey, run.period, run.viewRestaurantIds) }}>{t('common.retry')}</button></p>}

        {/* KPI Cards — 2-col compact grid on phones */}
        <div className="grid grid-cols-2 gap-3 sm:flex sm:flex-col sm:gap-6 min-[1250px]:flex-row">
          <div className="w-full min-[1250px]:flex-1">
            <StatCard
              {...metricIcon('spend')}
              title={t('products.sourcing.currentCost')}
              value={totalCurrentSpend}
              valueType="currency"
              loading={loading || !volumesReady}
              mobileLayout="tile"
            />
          </div>
          <div className="w-full min-[1250px]:flex-1">
            <StatCard
              icon={<PiggyBank className="w-6 h-6" style={{ color: '#079455' }} />}
              iconBg={iconBgGreen}
              title={t(totalSavings < 0 ? 'products.sourcing.costIncrease' : 'products.totalSavings')}
              value={Math.abs(totalSavings)}
              valueType="currency"
              loading={loading || !volumesReady}
              mobileLayout="tile"
            />
          </div>
          <div className="w-full min-[1250px]:flex-1">
            <StatCard
              icon={<Percent className="w-6 h-6" style={{ color: '#079455' }} />}
              iconBg={iconBgGreen}
              title={t('products.savingsPercent')}
              value={totalSavingsPercent}
              valueType="percentage"
              loading={loading || !volumesReady}
              mobileLayout="tile"
            />
          </div>
          <div className="w-full min-[1250px]:flex-1">
            <StatCard
              icon={<Package className="w-6 h-6" style={{ color: '#2E90FA' }} />}
              iconBg={iconBgBlue}
              title={t('orderHistory.totalProducts')}
              value={sortedEntries.length}
              valueType="number"
              loading={loading || !volumesReady}
              mobileLayout="tile"
            />
          </div>
        </div>

        {/* Loading state */}
        {loading && (
          <div className={cn(whiteBG, "rounded-xl flex items-center justify-center py-16")}>
            <Loader2 className="w-8 h-8 animate-spin text-text dark:text-text-dark" />
            <span className={cn(primaryText, smallTextSize, "ml-3")}>
              {t('products.searchingAlternatives')}
            </span>
          </div>
        )}

        {/* Product comparison cards */}
        {!loading && (
          <div className="flex flex-col gap-4">
            {sortedEntries.map(({ articleId, parent, selected, currentSpend, projectedSpend, savings, savingsPercent, isSameProduct, comparison }) => {
              const displayOption = isSameProduct ? parent : selected!
              const optionCount = tieredMap[articleId] ? countTieredOptions(tieredMap[articleId]) : 0
              const displayOptionSpend = isSameProduct ? currentSpend : projectedSpend

              return (
                <div
                  key={articleId}
                  className={cn(
                    whiteBG,
                    "rounded-xl cursor-pointer transition-all hover:shadow-md",
                    "border border-transparent hover:border-gray-200 dark:hover:border-gray-700"
                  )}
                  onClick={() => openDetail(articleId)}
                >
                  {/* Card layout: left product | arrow | right alternative | savings.
                      Stacks vertically on phones; desktop row re-asserted at sm. */}
                  <div className="flex flex-col sm:flex-row items-stretch">
                    {/* LEFT: Current product */}
                    <div className="flex-1 p-4 min-[900px]:p-5 min-w-0">
                      <div className="flex items-start gap-3">
                        {/* Platform logo */}
                        <div className="shrink-0 w-10 h-10 rounded-lg bg-gray-50 dark:bg-gray-800/50 flex items-center justify-center">
                          <PlatformLogo platform={parent.platform.toString()} size="medium" />
                        </div>
                        {/* Product info */}
                        <div className="min-w-0 flex-1">
                          <div className={cn(primaryText, smallTextSize, semiBoldText, "truncate")}>
                            {parent.name}
                          </div>
                          {renderVolume(articleId, parent)}
                          {renderEvidence(parent)}
                        </div>
                      </div>
                      {/* Unit price - main focus */}
                      <div className="mt-3 flex items-baseline gap-3">
                        <span className={cn(primaryText, largeTextSize, boldText)}>
                          {formatUnitPrice(parent)}
                        </span>
                        <span className={cn(tertiaryText, extraSmallTextSize)}>
                          {money(currentSpend)} {t('products.inPeriod')}
                        </span>
                      </div>
                    </div>

                    {/* ARROW divider */}
                    <div className="hidden sm:flex items-center px-2 min-[900px]:px-4">
                      <div className="w-8 h-8 rounded-full bg-gray-100 dark:bg-gray-800 flex items-center justify-center">
                        <ArrowRight className={cn(tertiaryText, "w-4 h-4")} />
                      </div>
                    </div>

                    {/* RIGHT: Best alternative */}
                    <div className="flex-1 p-4 min-[900px]:p-5 min-w-0">
                      <div className="flex items-start gap-3">
                        {/* Platform logo */}
                        <div className="shrink-0 w-10 h-10 rounded-lg bg-gray-50 dark:bg-gray-800/50 flex items-center justify-center">
                          <PlatformLogo platform={displayOption.platform.toString()} size="medium" />
                        </div>
                        {/* Product info */}
                        <div className="min-w-0 flex-1">
                          <div className="flex items-center gap-2">
                            <span className={cn(primaryText, smallTextSize, semiBoldText, "truncate")}>
                              {displayOption.name}
                            </span>
                            {optionCount > 0 && (
                              <span className={cn(
                                "shrink-0 text-xs px-1.5 py-0.5 rounded-full",
                                "bg-gray-100 dark:bg-gray-800 text-gray-500 dark:text-gray-400"
                              )}>
                                +{optionCount}
                              </span>
                            )}
                          </div>
                          <div className={cn(tertiaryText, extraSmallTextSize, "mt-0.5")}>
                            {isSameProduct
                              ? renderVolume(articleId, parent)
                              : renderEvidence(displayOption)}
                          </div>
                        </div>
                      </div>
                      {/* Unit price - main focus */}
                      <div className="mt-3 flex items-baseline gap-3">
                        <span className={cn(primaryText, largeTextSize, boldText)}>
                          {formatUnitPrice(displayOption)}
                        </span>
                        {comparison.approximate && <span className={cn(tertiaryText, extraSmallTextSize)}>{t('products.sourcing.approximate')}</span>}
                        <span className={cn(tertiaryText, extraSmallTextSize)}>
                          {money(displayOptionSpend)} {t('products.inPeriod')}
                        </span>
                      </div>
                    </div>

                    {/* SAVINGS badge */}
                    <div className={cn(
                      "shrink-0 flex flex-col items-center justify-center px-4 min-[900px]:px-6 py-3 sm:py-0 border-t sm:border-t-0 sm:border-l",
                      borderPrimary,
                      "min-w-[100px] min-[900px]:min-w-[120px]"
                    )}>
                      {volumesReady && savings !== null && savings !== 0 ? (
                        <>
                          <div className={cn(baseTextSize, boldText)} style={{ color: savings !== null && savings < 0 ? (darkMode ? '#FCA5A5' : '#ef4444') : (darkMode ? '#34D399' : '#16a34a') }}>
                            {savings > 0 ? '-' : '+'}{Math.abs(savingsPercent || 0).toFixed(0)}%
                          </div>
                          <div className={cn(smallTextSize, semiBoldText, "mt-0.5")} style={{ color: savings !== null && savings < 0 ? (darkMode ? '#FCA5A5' : '#ef4444') : (darkMode ? '#34D399' : '#16a34a') }}>
                            €{Math.abs(savings).toFixed(2)}
                          </div>
                          <div className={cn(tertiaryText, extraSmallTextSize, "mt-0.5")}>
                            {t(savings < 0 ? 'products.sourcing.costIncrease' : 'products.savings')}
                          </div>
                        </>
                      ) : (
                        <>
                          <div className={cn(tertiaryText, smallTextSize, semiBoldText)}>--</div>
                          <div className={cn(tertiaryText, extraSmallTextSize, "mt-1")}>
                            {savings === null ? t('products.sourcing.unavailable') : optionCount > 0 ? t('products.viewOptions') : t('products.noAlternatives')}
                          </div>
                        </>
                      )}
                    </div>
                  </div>
                </div>
              )
            })}
          </div>
        )}
      </div>

      {/* Side panel for all alternatives */}
      {detailArticleId && (
        <div
          className="fixed inset-0 z-30 flex justify-end bg-[#111827]/40 dark:bg-[#111827]/40 pt-[72px] md:pt-0"
          onClick={closeDetail}
        >
          <div
            className={cn(
              "h-full w-full max-w-full md:max-w-[560px] bg-white dark:bg-background-dark shadow-xl flex flex-col transform transition-all duration-250 ease-out",
              isPanelVisible ? "translate-x-0" : "translate-x-full"
            )}
            onClick={(e) => e.stopPropagation()}
          >
            {/* Panel header with current product info */}
            <div className={cn("px-5 py-5 border-b", borderPrimary)}>
              <div className="flex items-center justify-between mb-4">
                <span className={cn(tertiaryText, extraSmallTextSize, "uppercase tracking-wide font-medium")}>
                  {t('products.alternatives')}
                </span>
                <button onClick={closeDetail} className={cn(tertiaryText, "p-1 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-800")}>
                  <X className="w-5 h-5" />
                </button>
              </div>
              {detailParent && detailArticleId && (
                <div className="flex items-start gap-3">
                  <div className="shrink-0 w-10 h-10 rounded-lg bg-gray-50 dark:bg-gray-800/50 flex items-center justify-center">
                    <PlatformLogo platform={detailParent.platform.toString()} size="medium" />
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className={cn(primaryText, mediumText, semiBoldText, "truncate")}>
                      {detailParent.name}
                    </div>
                    {renderVolume(detailArticleId, detailParent)}
                    <div className="flex items-baseline gap-3 mt-1">
                      <span className={cn(primaryText, baseTextSize, boldText)}>
                        {formatUnitPrice(detailParent)}
                      </span>
                      <span className={cn(tertiaryText, extraSmallTextSize)}>
                        {money(detailCurrentSpend)} {t('products.inPeriod')}
                      </span>
                    </div>
                  </div>
                </div>
              )}
            </div>

            {/* Tiered options list */}
            <div className="flex-1 overflow-y-auto p-4 flex flex-col gap-5">
              {detailParent && renderOption({ ...detailParent, matchScore: null }, -1, true)}
              {detailTiers && tierSection(t('products.tierCheaper'), null, detailTiers.cheaper, true)}
              {detailTiers && tierSection(t('products.tierUncomparable'), t('products.sourcing.incompatible'), detailTiers.uncomparable, false)}
              {detailTiers && tierSection(t('products.tierMoreExpensive'), null, detailTiers.expensive, true)}

              {(!detailTiers || countTieredOptions(detailTiers) === 0) && (
                <div className={cn("flex items-center justify-center py-8", tertiaryText, smallTextSize)}>
                  {t('products.noAlternatives')}
                </div>
              )}
              <div className="flex flex-col gap-3 border-t pt-4 border-gray-200 dark:border-gray-700">
                <label className={cn(primaryText, smallTextSize)}>{t('products.sourcing.search')}
                  <input value={query} onChange={event => setQuery(event.target.value)} className={cn(whiteBG, borderPrimary, 'mt-2 w-full rounded-lg border px-3 py-2')} placeholder={t('products.sourcing.searchPlaceholder')} />
                </label>
                <div className="flex flex-wrap gap-2">
                  <select aria-label={t('products.sourcing.categoryFilter')} value={category} onChange={event => { setCategory(event.target.value); setSearchLimit(20) }} className={cn(whiteBG, borderPrimary, primaryText, smallTextSize, 'min-w-0 flex-1 rounded-lg border p-2')}>
                    <option value="">{t('products.sourcing.allCategories')}</option>
                    {categories.map(value => <option key={value} value={value}>{getCategoryDisplayName(value)}</option>)}
                  </select>
                  <select aria-label={t('products.sourcing.supplierFilter')} value={supplier} onChange={event => { setSupplier(event.target.value); setSearchLimit(20) }} className={cn(whiteBG, borderPrimary, primaryText, smallTextSize, 'min-w-0 flex-1 rounded-lg border p-2')}>
                    <option value="">{t('products.sourcing.allSuppliers')}</option>
                    {suppliers.map(value => <option key={value} value={value}>{value}</option>)}
                  </select>
                </div>
                {search.results.map((product, index) => renderOption({ ...product, matchScore: snapshot?.options[detailArticleId]?.find(option => option.productKey === product.key)?.matchScore ?? null }, index, true))}
                {search.totalMatches === 0 && <p className={cn(tertiaryText, smallTextSize)}>{t('products.sourcing.noSearchResults')}</p>}
                {search.totalMatches > searchLimit && <button onClick={() => setSearchLimit(limit => limit + 20)} className={cn(primaryText, smallTextSize, 'underline')}>{t('products.sourcing.loadMore')}</button>}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
